import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { getPaymentStatus } from '../lib/paymentsApi';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let attempts = 0;
    // Fast phase (8x @ 1s) covers the common case where Stripe's redirect
    // and our webhook race is already resolved; slow phase (10x @ 3s)
    // covers webhook delivery lagging a few extra seconds.
    const poll = async () => {
      try {
        const s = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (s.payment_status === 'paid') { setStatus('paid'); return; }
        attempts += 1;
        if (attempts < 8) setTimeout(poll, 1000);
        else if (attempts < 18) setTimeout(poll, 3000);
        else setStatus('pending');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, retryKey]);

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center space-y-4">
        {status === 'checking' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto" />
            <p className="text-sm text-neutral-600" data-testid="donation-checking-message">Confirming your donation…</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <Heart className="w-10 h-10 text-orange-600 mx-auto" fill="currentColor" />
            <h1 className="text-xl font-semibold text-neutral-900" data-testid="donation-success-title">Thank you!</h1>
            <p className="text-sm text-neutral-500">Your donation helps keep Waxify running. We really appreciate it.</p>
          </>
        )}
        {status === 'pending' && (
          <>
            <p className="text-sm text-neutral-500" data-testid="donation-pending-message">
              Still confirming with Stripe — if you completed payment, you're all set. Thank you!
            </p>
            <button
              type="button"
              onClick={() => { setStatus('checking'); setRetryKey((k) => k + 1); }}
              className="text-sm text-orange-600 hover:text-orange-700 underline"
              data-testid="donation-check-again-button"
            >
              Check again
            </button>
          </>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-500" data-testid="donation-error-message">
            We couldn't confirm this donation. If you were charged, please contact us.
          </p>
        )}
        <Link to="/" className="inline-block text-sm text-orange-600 hover:text-orange-700 underline" data-testid="donation-success-back-link">
          Back to Waxify
        </Link>
      </div>
    </div>
  );
}
